<template>
    <grid-area area="rail">
        <navigation-rail>
            <template #header>
                <a :href="createLegacyUrl('dashboard')" title="Ga naar dashboard">
                    <flex-container
                        inline
                        justify-content="center"
                        :gap="null"
                    >
                        <svg-icon
                            :icon="logo"
                            :size="IconSize.lg"
                            is-custom-icon
                        />
                        <component
                            :is="badge"
                            v-if="badge"
                            style="width: 80%; padding-top: 5px;"
                        />
                        <span class="screen-reader">Ga naar dashboard</span>
                    </flex-container>
                </a>

                <navigation-button
                    :icon="PersonAddAltIcon"
                    :href="createLegacyUrl('dashboard?create-lead')"
                    label="Nieuwe lead"
                />
            </template>

            <template #default>
                <navigation-rail-item
                    show-label
                    label="Dashboard"
                    :icon="OverviewIcon"
                    :href="createLegacyUrl('dashboard')"
                />

                <navigation-rail-item
                    v-if="route.params.contactId"
                    show-label
                    label="Lead"
                    :icon="GroupsIcon"
                    :to="{
                        name: 'contactDetail',
                        params: {
                            contactId: route.params.contactId,
                        },
                    }"
                />

                <navigation-rail-item
                    show-label
                    label="Overzicht"
                    :icon="PatientListIcon"
                    :href="createLegacyUrl('tasks/all')"
                />

                <navigation-rail-item
                    v-if="currentLegacyUser?.canViewReports"
                    show-label
                    label="Rapportages"
                    :icon="MonitoringIcon"
                    :href="createLegacyUrl('reports')"
                />

                <navigation-rail-item
                    show-label
                    label="Documenten"
                    :icon="DescriptionIcon"
                    :to="{
                        name: 'documentList',
                    }"
                />

                <navigation-rail-item
                    v-if="companyEnabledOrders"
                    show-label
                    label="Opdrachten"
                    :icon="BusinessCenterIcon"
                    :href="createLegacyUrl('orders/all')"
                />

                <navigation-rail-item
                    v-if="companyEnabledTypeform"
                    show-label
                    label="Enquêtes"
                    :icon="QuizIcon"
                    :href="createLegacyUrl('typeform/responses')"
                />

                <navigation-rail-item
                    v-if="isCompanyAdmin"
                    show-label
                    label="MaQr"
                    :icon="StoreIcon"
                    :href="createLegacyUrl('marketplace')"
                />
            </template>

            <template #footer>
                <navigation-rail-item
                    label="Support"
                    :icon="HelpIcon"
                    href="https://support.keyvue.nl/hc/nl/"
                />
            </template>
        </navigation-rail>

        <navigation-rail v-if="contact" variant="icon">
            <template #header>
                <user-avatar
                    :placeholder="contact.name.abbreviation"
                    :alt="contact.name.fullName"
                    style="margin-top: 92px;"
                />
            </template>
            <template #default>
                <navigation-rail-item
                    label="Contact Detail"
                    :icon="ContactPageIcon"
                    :to="{
                        name: 'contactDetail',
                        params: {
                            contactId: route.params.contactId,
                        },
                    }"
                />
                <navigation-rail-item
                    v-if="false"
                    label="Objects"
                    :icon="HomeIcon"
                    :to="{ name: 'contactList' }"
                />
                <navigation-rail-item
                    v-if="false"
                    label="Mail"
                    :icon="MailIcon"
                    href="https://app.keyvue.com"
                />
            </template>
        </navigation-rail>
    </grid-area>
</template>

<script setup lang="ts">
    import {
        IconSize,
        UserAvatar,
        SvgIcon,
        NavigationButton,
        NavigationRail,
        NavigationRailItem,
        GridArea,
    } from '@keyvue/one-ui';

    import { CredentialsType, TypeformCredentials } from '@keyvue/one-api-client-legacy';

    import PersonAddAltIcon from '@keyvue/one-material-icons/icons/person_add.svg?component';
    import OverviewIcon from '@keyvue/one-material-icons/icons/overview.svg?component';
    import GroupsIcon from '@keyvue/one-material-icons/icons/groups.svg?component';
    import MonitoringIcon from '@keyvue/one-material-icons/icons/monitoring.svg?component';
    import DescriptionIcon from '@keyvue/one-material-icons/icons/description.svg?component';
    import BusinessCenterIcon from '@keyvue/one-material-icons/icons/business_center.svg?component';
    import QuizIcon from '@keyvue/one-material-icons/icons/quiz.svg?component';
    import StoreIcon from '@keyvue/one-material-icons/icons/store.svg?component';
    import HelpIcon from '@keyvue/one-material-icons/icons/help.svg?component';
    import ContactPageIcon from '@keyvue/one-material-icons/icons/contact_page.svg?component';
    import HomeIcon from '@keyvue/one-material-icons/icons/home.svg?component';
    import MailIcon from '@keyvue/one-material-icons/icons/mail.svg?component';
    import PatientListIcon from '@keyvue/one-material-icons/icons/patient_list.svg?component';

    import KeyvueLogo from '@assets/logo.svg?component';
    import MaQrLogo from '@assets/logo-maqr.svg?component';
    import BadgeLegacy from '@assets/badges/legacy.svg?component';
    import BadgeBasic from '@assets/badges/basic.svg?component';
    import BadgeBasicPlus from '@assets/badges/basic-plus.svg?component';
    import BadgePremium from '@assets/badges/premium.svg?component';
    import BadgePremiumPlus from '@assets/badges/premium-plus.svg?component';
    import BadgePoweredByKeyvue from '@assets/badges/powered-by-keyvue.svg?component';

    import { useRoute } from 'vue-router';
    import { Contact } from '@keyvue/one-api-client';
    import useClient from '@composables/useClient';
    import { computed, ref } from 'vue';
    import createLegacyUrl from '@/utils/legacyUrls';

    const { client, currentLegacyCompany, currentLegacyUser } = useClient();
    const route = useRoute();
    const contact = ref<Contact | null>(null);

    if (route.params.contactId) {
        contact.value = await client.get(Contact, route.params.contactId.toString());
    }

    const companyEnabledOrders = computed(() => {
        if (!currentLegacyCompany.value?.orderSettings) {
            return false;
        }

        return currentLegacyCompany.value?.orderSettings.enabled;
    });

    const companyEnabledTypeform = computed(() => {
        const credentials = currentLegacyCompany.value?.getCredentialsWithType(CredentialsType.TYPEFORM) as TypeformCredentials[] ?? [];
        if (credentials.length === 0) {
            return false;
        }

        return credentials[0].workspaceId !== null;
    });

    const isCompanyAdmin = computed(() => currentLegacyUser.value?.isCompanyManager ?? false);

    const logo = computed(() => {
        switch (currentLegacyCompany.value?.licenseType) {
        case 'maqr-only': {
            return MaQrLogo;
        }
        default:
            return KeyvueLogo;
        }
    });

    const badge = computed(() => {
        switch (currentLegacyCompany.value?.licenseType) {
        case 'branch-license': {
            return BadgeLegacy;
        }
        case 'maqr-only': {
            return BadgePoweredByKeyvue;
        }
        case 'basic': {
            return BadgeBasic;
        }
        case 'basic-plus': {
            return BadgeBasicPlus;
        }
        case 'premium': {
            return BadgePremium;
        }
        case 'premium-plus': {
            return BadgePremiumPlus;
        }
        default:
            return false;
        }
    });
</script>

<style lang="scss">
.navigation-rail--icon-highlight .navigation-rail__header {
    height: auto !important;

    a {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
}

.navigation-rail--icon .navigation-rail__header {
    height: 132px;
    justify-content: space-between;
}
</style>
